import React from "react";
import Header from "../components/Header";

export default function PipePrefabricationSystem() {
  return (
    <>
    <Header />
    <div className="w-full min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 p-4 md:p-6 font-sans">
      <div className="max-w-7xl mx-auto bg-white shadow-2xl p-6 md:p-8 rounded-2xl">
        {/* Enhanced Header */}
        <div className="flex items-center space-x-4 md:space-x-6">
          <div className="relative">
            <div className="w-12 h-24 bg-gradient-to-b from-blue-500 to-blue-800 rounded-xl shadow-lg"></div>
            <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-orange-500 rounded-full border-4 border-white"></div>
          </div>
          <div>
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-900 to-blue-600 bg-clip-text text-transparent">
              4-Pipe Prefabrication System
            </h1>
            <p className="text-sm md:text-base text-gray-600 mt-2">
              End-to-End Industrial Pipe Manufacturing & Management Platform
            </p>
          </div>
        </div>

        {/* Enhanced System Description */}
        <div className="mt-10 bg-gradient-to-r from-blue-900 to-blue-700 text-white rounded-2xl shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-blue-800 to-blue-900 text-center py-4 text-xl font-bold">
            <div className="flex items-center justify-center gap-3">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
              </svg>
              System Overview
            </div>
          </div>
          <div className="p-6 md:p-8">
            <p className="text-base md:text-lg leading-relaxed">
              The pipe prefabrication system begins with intelligent drawing management, generating comprehensive BOM lists through automated drawing analysis. By integrating with warehouse systems, it performs real-time material simulation and optimized storage allocation. The system orchestrates the entire prefabrication workflow through detailed job cards, enabling full-process management of welding, NDT, anti-corrosion painting, and final transportation to site.
            </p>
            <div className="flex flex-wrap gap-3 mt-6">
              <span className="px-4 py-2 bg-blue-600 rounded-full text-sm font-medium">Drawing Management</span>
              <span className="px-4 py-2 bg-blue-600 rounded-full text-sm font-medium">Material Simulation</span>
              <span className="px-4 py-2 bg-blue-600 rounded-full text-sm font-medium">Process Automation</span>
              <span className="px-4 py-2 bg-blue-600 rounded-full text-sm font-medium">Quality Control</span>
            </div>
          </div>
        </div>

        {/* Enhanced Flow Diagram */}
        <div className="mt-12 bg-gradient-to-br from-gray-50 to-blue-50 border border-gray-200 p-6 md:p-8 rounded-2xl shadow-lg">
          <h2 className="text-2xl font-bold text-gray-800 mb-8 text-center">Prefabrication Workflow</h2>
          
          {/* Main Process Flow */}
          <div className="relative">
            {/* Warehouse Management Section */}
            <div className="mb-10">
              <div className="bg-gradient-to-r from-orange-600 to-orange-500 text-white py-3 px-6 rounded-xl shadow-md inline-block">
                <div className="flex items-center gap-2 font-semibold">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                  </svg>
                  Warehouse Management System
                </div>
              </div>
              <div className="mt-4">
                <div className="inline-block px-4 py-2 bg-gradient-to-r from-orange-100 to-orange-50 text-orange-700 rounded-lg font-medium">
                  Materials Simulation & Storage Allocation
                </div>
              </div>
            </div>

            {/* Drawing Management - Vertical Label */}
            <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
              <div className="bg-gradient-to-b from-red-600 to-red-700 text-white px-4 py-8 rounded-xl shadow-lg -rotate-90 whitespace-nowrap font-bold text-sm">
                <div className="rotate-90">Drawing Management</div>
              </div>
            </div>

            {/* Process Steps Grid */}
            <div className="ml-24 md:ml-32">
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="bg-gradient-to-br from-blue-100 to-white p-4 rounded-xl shadow-md border border-blue-200">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                      <span className="text-white text-sm">⚡</span>
                    </div>
                    <h3 className="font-bold text-blue-900">Joint Welding</h3>
                  </div>
                  <p className="text-sm text-gray-600">Automated welding process with quality monitoring</p>
                </div>
                
                <div className="bg-gradient-to-br from-blue-100 to-white p-4 rounded-xl shadow-md border border-blue-200">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                      <span className="text-white text-sm">🎨</span>
                    </div>
                    <h3 className="font-bold text-blue-900">Anti-Corrosion Painting</h3>
                  </div>
                  <p className="text-sm text-gray-600">Coating application with thickness monitoring</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-green-100 to-white p-4 rounded-xl shadow-md border border-green-200">
                  <h4 className="font-semibold text-green-900 mb-1">Job Card</h4>
                  <p className="text-xs text-gray-600">Digital work instructions</p>
                </div>
                
                <div className="bg-gradient-to-br from-purple-100 to-white p-4 rounded-xl shadow-md border border-purple-200">
                  <h4 className="font-semibold text-purple-900 mb-1">NDT Testing</h4>
                  <p className="text-xs text-gray-600">Non-destructive examination</p>
                </div>
                
                <div className="bg-gradient-to-br from-orange-100 to-white p-4 rounded-xl shadow-md border border-orange-200">
                  <h4 className="font-semibold text-orange-900 mb-1">Progress Tracking</h4>
                  <p className="text-xs text-gray-600">Real-time status updates</p>
                </div>
              </div>
            </div>

            {/* Transportation Section */}
            <div className="mt-12 text-center">
              <div className="inline-block bg-gradient-to-r from-red-600 to-red-700 text-white px-8 py-4 rounded-xl shadow-lg">
                <div className="flex items-center justify-center gap-3 font-bold text-lg">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                    <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1v-1h4v1a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H20a1 1 0 001-1v-4a1 1 0 00-.293-.707l-2-2A1 1 0 0017 7h-3.172a1 1 0 00-.707.293l-1.414 1.414A1 1 0 0011.172 9H8a1 1 0 00-1 1v3a1 1 0 001 1z" />
                  </svg>
                  Pipe Segment Transportation
                </div>
                <p className="text-sm opacity-90 mt-2">Tracked delivery to site with logistics integration</p>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Image Gallery */}
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Manufacturing Interface */}
          <div className="rounded-2xl shadow-2xl overflow-hidden bg-white transform transition-transform hover:scale-[1.02]">
            <div className="bg-gradient-to-r from-blue-800 to-blue-600 text-white py-4 px-6 font-bold">
              <div className="flex items-center gap-3">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                </svg>
                Manufacturing Control Interface
              </div>
            </div>
            <div className="p-4">
              <img
                src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
                alt="Industrial Pipe Manufacturing Control System"
                className="w-full h-72 object-cover rounded-lg"
              />
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <h3 className="font-semibold text-blue-900 mb-2">Real-time Production Monitoring</h3>
                <p className="text-sm text-gray-600">
                  Centralized interface for monitoring all prefabrication processes including welding parameters, NDT results, and coating applications with real-time quality control data.
                </p>
              </div>
            </div>
          </div>

          {/* Workflow Visualization */}
          <div className="rounded-2xl shadow-2xl overflow-hidden bg-white transform transition-transform hover:scale-[1.02]">
            <div className="bg-gradient-to-r from-blue-800 to-blue-600 text-white py-4 px-6 font-bold">
              <div className="flex items-center gap-3">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 5a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2h-2.22l.123.489.804.804A1 1 0 0113 18H7a1 1 0 01-.707-1.707l.804-.804L7.22 15H5a2 2 0 01-2-2V5zm5.771 7H5V5h10v7H8.771z" clipRule="evenodd" />
                </svg>
                Digital Workflow Visualization
              </div>
            </div>
            <div className="p-4">
              <img
                src="https://images.unsplash.com/photo-1581094794329-c8112a89af12?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
                alt="Pipe Prefabrication Workflow Diagram"
                className="w-full h-72 object-cover rounded-lg"
              />
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <h3 className="font-semibold text-blue-900 mb-2">Complete Process Integration</h3>
                <p className="text-sm text-gray-600">
                  Visual representation of the entire pipe prefabrication workflow from drawing analysis to final transportation, showing interdependencies and progress status across all stages.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* System Benefits */}
        <div className="mt-12 bg-gradient-to-r from-blue-50 to-gray-50 rounded-2xl p-6 md:p-8 border border-blue-100">
          <h2 className="text-2xl font-bold text-blue-900 mb-8 text-center">System Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-5 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <span className="text-2xl">⏱️</span>
              </div>
              <h3 className="font-bold text-gray-800 mb-2">40% Time Reduction</h3>
              <p className="text-sm text-gray-600">Automated processes reduce manual coordination time</p>
            </div>
            
            <div className="bg-white p-5 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <span className="text-2xl">✅</span>
              </div>
              <h3 className="font-bold text-gray-800 mb-2">99.5% Quality Rate</h3>
              <p className="text-sm text-gray-600">Automated quality checks and NDT integration</p>
            </div>
            
            <div className="bg-white p-5 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <span className="text-2xl">💰</span>
              </div>
              <h3 className="font-bold text-gray-800 mb-2">30% Cost Savings</h3>
              <p className="text-sm text-gray-600">Optimized material usage and reduced rework</p>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-white p-4 rounded-xl shadow-md border border-blue-100">
            <div className="text-2xl font-bold text-blue-800">5,000+</div>
            <div className="text-sm text-gray-600">Pipes Managed Monthly</div>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-white p-4 rounded-xl shadow-md border border-blue-100">
            <div className="text-2xl font-bold text-blue-800">95%</div>
            <div className="text-sm text-gray-600">First-time Quality Rate</div>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-white p-4 rounded-xl shadow-md border border-blue-100">
            <div className="text-2xl font-bold text-blue-800">24/7</div>
            <div className="text-sm text-gray-600">Production Monitoring</div>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-white p-4 rounded-xl shadow-md border border-blue-100">
            <div className="text-2xl font-bold text-blue-800">99.9%</div>
            <div className="text-sm text-gray-600">Material Traceability</div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}